import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Primary markets are bundled eagerly so the first paint never flashes a fallback
// for the common case. The other 16 locales (~270KB) are code-split and fetched
// on demand — they no longer weigh down the initial bundle for every visitor.
import en from '../locales/en.json';
import it from '../locales/it.json';

// One dynamic import per lazy locale → Vite emits a separate chunk for each.
const localeLoaders: Record<string, () => Promise<any>> = {
  es: () => import('../locales/es.json'),
  tr: () => import('../locales/tr.json'),
  el: () => import('../locales/el.json'),
  ar: () => import('../locales/ar.json'),
  hi: () => import('../locales/hi.json'),
  bn: () => import('../locales/bn.json'),
  ru: () => import('../locales/ru.json'),
  id: () => import('../locales/id.json'),
  sw: () => import('../locales/sw.json'),
  nl: () => import('../locales/nl.json'),
  pl: () => import('../locales/pl.json'),
  uk: () => import('../locales/uk.json'),
  vi: () => import('../locales/vi.json'),
  th: () => import('../locales/th.json'),
  ko: () => import('../locales/ko.json'),
  ms: () => import('../locales/ms.json'),
};

// Fetch a lazy locale and register it. Safe to call repeatedly; failures fall
// through to the always-present English bundle so the UI never breaks.
async function ensureLocale(lng: string): Promise<void> {
  if (!lng || i18n.hasResourceBundle(lng, 'translation')) return;
  const loader = localeLoaders[lng];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
  } catch { /* graceful: stay on the English fallback */ }
}

const LANG_MAP: Record<string, string> = {
  en: 'en', 'en-US': 'en', 'en-GB': 'en',
  it: 'it', 'it-IT': 'it',
  es: 'es', 'es-ES': 'es', 'es-MX': 'es', 'es-AR': 'es',
  tr: 'tr', 'tr-TR': 'tr',
  el: 'el', 'el-GR': 'el',
  ar: 'ar', 'ar-SA': 'ar', 'ar-EG': 'ar', 'ar-AE': 'ar', 'ar-MA': 'ar',
  hi: 'hi', 'hi-IN': 'hi',
  bn: 'bn', 'bn-BD': 'bn', 'bn-IN': 'bn',
  ru: 'ru', 'ru-RU': 'ru',
  id: 'id', 'id-ID': 'id',
  sw: 'sw', 'sw-KE': 'sw', 'sw-TZ': 'sw',
  nl: 'nl', 'nl-NL': 'nl', 'nl-BE': 'nl',
  pl: 'pl', 'pl-PL': 'pl',
  uk: 'uk', 'uk-UA': 'uk',
  vi: 'vi', 'vi-VN': 'vi',
  th: 'th', 'th-TH': 'th',
  ko: 'ko', 'ko-KR': 'ko',
  ms: 'ms', 'ms-MY': 'ms',
};

const ready = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      it: { translation: it },
    },
    // Bundled resources are partial — more locales are added at runtime.
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: ['en', 'it', 'es', 'tr', 'el', 'ar', 'hi', 'bn', 'ru', 'id', 'sw', 'nl', 'pl', 'uk', 'vi', 'th', 'ko', 'ms'],
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupCookie: 'mt_lang',
      lookupLocalStorage: 'mt_lang',
      caches: ['localStorage', 'cookie'],
      cookieMinutes: 525600, // 1 year
      convertDetectedLanguage: (lng: string) => LANG_MAP[lng] || lng.split('-')[0],
    },
    interpolation: {
      escapeValue: false,
    },
    // Render immediately with the available (fallback) strings and re-render once
    // a lazily-loaded locale arrives — never suspend on translations.
    react: {
      useSuspense: false,
    },
  });

// When the user switches languages, fetch the bundle first, then re-apply it so
// the tree re-renders with the freshly loaded strings (no recursion: the second
// changeLanguage sees the bundle already present and the guard short-circuits).
i18n.on('languageChanged', (lng) => {
  if (lng && !i18n.hasResourceBundle(lng, 'translation') && localeLoaders[lng]) {
    ensureLocale(lng).then(() => i18n.changeLanguage(lng));
  }
});

// Load the initially-detected language (if it's one of the lazy ones), then apply.
ready.then(() => {
  const detected = i18n.language;
  if (detected && !i18n.hasResourceBundle(detected, 'translation')) {
    ensureLocale(detected).then(() => i18n.changeLanguage(detected));
  }
});

export default i18n;
export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ms', label: 'Bahasa Melayu', flag: '🇲🇾' },
];
